<template>
  <div class="recommendation-personas">
    <Sidebar />
    <div class="content-area">
      <h1>Recommendation<br>Personas</h1>
      
      <div v-if="loading" class="loading">
        Loading personas...
      </div>
      
      <div v-else-if="error" class="error">
        {{ error }}
      </div>
      
      <div v-else class="personas-list">
        <table class="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Persona</th>
              <th>Assessment Option</th>
              <th>Weight</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in recommendationPersonas" :key="item.id">
              <td>{{ item.id }}</td>
              <td>{{ item.persona.name }}</td>
              <td>{{ item.assessment_option.value }}</td>
              <td>{{ item.weight }}</td>
              <td class="actions-cell">
                <button @click="editPersona(item)" class="btn btn-sm btn-primary">Edit</button>
                <button @click="deletePersona(item.id)" class="btn btn-sm btn-danger ml-2">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
        
        <div class="add-button-container">
          <button @click="showAddModal = true" class="btn btn-success mt-3">
            Add New Recommendation Persona
          </button>
        </div>
      </div>
    </div>
    
    <!-- Add/Edit Modal -->
    <div v-if="showAddModal || showEditModal" class="modal-overlay">
      <div class="modal-content">
        <h3>{{ showEditModal ? 'Edit' : 'Add' }} Recommendation Persona</h3>
        
        <div class="form-group">
          <label>Persona</label>
          <select v-model="formData.persona_id" class="form-control">
            <option v-for="persona in personas" :key="persona.id" :value="persona.id">
              {{ persona.name }}
            </option>
          </select>
        </div>
        
        <div class="form-group">
          <label>Assessment Option</label>
          <select v-model="formData.option_id" class="form-control">
            <option v-for="option in assessmentOptions" :key="option.id" :value="option.id">
              {{ option.value }}
            </option>
          </select>
        </div>
        
        <div class="form-group">
          <label>Weight</label>
          <input type="number" v-model="formData.weight" class="form-control" min="1" max="10">
        </div>
        
        <div class="modal-actions">
          <button @click="savePersona" class="btn btn-primary">Save</button>
          <button @click="closeModal" class="btn btn-secondary ml-2">Cancel</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Sidebar from '@/components/Sidebar.vue'

// Replace API_URL import with direct URL
const API_URL = 'https://api.kewaar.com/api'

export default {
  name: 'RecommendationPersonas',
  components: {
    Sidebar
  },
  data() {
    return {
      recommendationPersonas: [],
      personas: [],
      assessmentOptions: [],
      loading: true,
      error: null,
      showAddModal: false,
      showEditModal: false,
      formData: {
        persona_id: null,
        option_id: null,
        weight: 1
      },
      editId: null
    }
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      this.error = null
      
      try {
        // Use direct API URL without concatenating /api again
        const response = await axios.get(`${API_URL}/recommendation-personas`)
        
        if (response.data.status === 'success') {
          this.recommendationPersonas = response.data.data
          
          // Extract unique personas
          const uniquePersonas = {}
          this.recommendationPersonas.forEach(item => {
            if (item.persona) {
              uniquePersonas[item.persona.id] = item.persona
            }
          })
          this.personas = Object.values(uniquePersonas)
          
          // Extract unique assessment options
          const uniqueOptions = {}
          this.recommendationPersonas.forEach(item => {
            if (item.assessment_option) {
              uniqueOptions[item.assessment_option.id] = item.assessment_option
            }
          })
          this.assessmentOptions = Object.values(uniqueOptions)
        } else {
          this.error = 'Failed to load recommendation personas'
        }
      } catch (err) {
        this.error = `Error: ${err.message || 'Failed to fetch data'}`
      } finally {
        this.loading = false
      }
    },
    
    editPersona(item) {
      this.formData = {
        persona_id: item.persona_id,
        option_id: item.option_id,
        weight: item.weight
      }
      this.editId = item.id
      this.showEditModal = true
    },
    
    async deletePersona(id) {
      if (!confirm('Are you sure you want to delete this recommendation persona?')) {
        return
      }
      
      try {
        const response = await axios.delete(`${API_URL}/recommendation-personas/${id}`)
        
        if (response.data.status === 'success') {
          this.recommendationPersonas = this.recommendationPersonas.filter(item => item.id !== id)
        } else {
          alert('Failed to delete recommendation persona')
        }
      } catch (err) {
        alert(`Error: ${err.message || 'Failed to delete data'}`)
      }
    },
    
    async savePersona() {
      try {
        let response
        
        if (this.showEditModal) {
          response = await axios.put(`${API_URL}/recommendation-personas/${this.editId}`, this.formData)
        } else {
          response = await axios.post(`${API_URL}/recommendation-personas`, this.formData)
        }
        
        if (response.data.status === 'success') {
          await this.fetchData()
          this.closeModal()
        } else {
          alert('Failed to save recommendation persona')
        }
      } catch (err) {
        alert(`Error: ${err.message || 'Failed to save data'}`)
      }
    },
    
    closeModal() {
      this.showAddModal = false
      this.showEditModal = false
      this.formData = {
        persona_id: null,
        option_id: null,
        weight: 1
      }
      this.editId = null
    }
  }
}
</script>

<style scoped>
.recommendation-personas {
  display: flex;
  height: 100vh;
}

.content-area {
  flex: 1;
  padding-left: 250px;
  overflow-x: auto;
}

.personas-list {
  padding: 20px 30px;
  width: 100%;
}

.add-button-container {
  padding: 20px 0;
}

.loading, .error {
  padding: 20px;
  text-align: center;
}

.error {
  color: red;
}

h1 {
  padding: 25px 30px 15px;
  font-size: 32px;
  color: #333;
  font-weight: 600;
  line-height: 1.2;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 25px;
  border-radius: 8px;
  max-width: 500px;
  width: 100%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-control {
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.modal-actions {
  margin-top: 25px;
  text-align: right;
}

.btn {
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  font-weight: 500;
}

.btn-sm {
  padding: 5px 10px;
  font-size: 14px;
}

.btn-primary {
  background-color: #0d6efd;
  color: white;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-success {
  background-color: #28a745;
  color: white;
}

.btn-danger {
  background-color: #dc3545;
  color: white;
}

.ml-2 {
  margin-left: 10px;
}

.mt-3 {
  margin-top: 15px;
}

.table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
  background-color: #fff;
  border-radius: 8px;
  overflow: hidden;
}

.table th, .table td {
  padding: 15px 20px;
  border-bottom: 1px solid #eee;
  text-align: left;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

.actions-cell {
  white-space: nowrap;
}

.table tr:hover {
  background-color: #f5f5f5;
}

.table tr:last-child td {
  border-bottom: none;
}

button.btn {
  transition: all 0.2s;
}

button.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}
</style> 